import {
  AdminPanelSettings,
  Analytics,
  Book,
  BookOnlineTwoTone,
  CalendarMonth,
  CalendarMonthSharp,
  CalendarMonthTwoTone,
  CalendarToday,
  Event,
  Chat,
  ChatBubbleOutline,
  Dashboard,
  DashboardCustomize,
  DashboardCustomizeTwoTone,
  DocumentScannerTwoTone,
  Inventory,
  LegendToggleRounded,
  ListAlt,
  ListAltOutlined,
  MonetizationOn,
  Money,
  People,
  PeopleAltTwoTone,
  PictureInPicture,
  PowerOff,
  ReceiptLong,
  RouteOutlined,
  SafetyCheck,
  SchoolSharp,
  Score,
  Settings,
  MenuBook,
  Speaker,
  Summarize,
  VerifiedUserTwoTone,
  PersonAdd,
  PeopleAlt,
  Group,
  SupervisorAccount,
  Person,
} from '@mui/icons-material';

export enum PageName {
  auth = 'auth',
  logout = 'logout',
  dashboard = 'dashboard',
  summary = 'summary',
  people = 'people',
  notes = 'notes',
  students = 'students',
  fees = 'fees',
  staff = 'staff',
  studentList = 'student-list',
  staffList = 'staff-list',
  payouts = 'payouts',
  details = 'details',
  attendance = 'attendance',
  calendar = 'calendar',
  enrollment = 'enrollment',
  document = 'documents',
  scores = 'scores',
  schedule = 'schedule',
  academics = 'academics',
  curriculum = 'curriculum',
  schedules = 'schedules',
  lessonPlans = 'lesson-plans',
  materials = 'materials',
  assessments = 'assessments',
  library = 'library',
  communications = 'communications',
  messaging = 'messaging',
  announcements = 'announcements',
  events = 'events',
  meetings = 'meetings',
  finance = 'finance',
  feeManagement = 'fee-management',
  financeList = 'finance-list',
  scholarships = 'scholarships',
  financialReports = 'financial-reports',
  administration = 'administration',
  transport = 'transport',
  inventory = 'inventory',
  healthAndSafety = 'health-and-safety',
  analytics = 'analytics',
  performance = 'performance',
  customDashboards = 'custom-dashboards',
  settings = 'settings',
  userManagement = 'user-management',
  customization = 'customization',
  complianceManagement = 'compliance-management',
  privacy = 'privacy',
  profile = 'profile',
  personalDetails = 'personal-details',
  documents = 'documents',
  leaves = 'leaves',
  users = 'users',
  parents = 'parents',
  visitors = 'visitors',
  guests = 'guests',
  guardian = 'guardian',
  homework = 'homework',
  eventsAndCommunication = 'events-communication',
  concerns = 'concerns',
}

export const getAuthPageDetails = () => ({
  [PageName.auth]: {
    path: '/',
    aliasPath: '/login',
    title: 'Login',
  },
  [PageName.logout]: {
    path: '/',
    aliasPath: '/logout',
    title: 'Logout',
    icon: <PowerOff />,
  },
});

export const getDashBoardPageDetails = () => ({
  [PageName.dashboard]: {
    path: '/' + PageName.dashboard,
    title: 'Dashboard',
    icon: <Dashboard />,
    subPages: {
      [PageName.finance]: {
        path: '/' + PageName.finance, //defaults to parent path
        title: 'finance-overview',
        icon: <Summarize />,
      },
      [PageName.people]: {
        path: '/' + PageName.people,
        icon: <ListAlt />,
        title: 'People',
      },
      [PageName.events]: {
        path: '/' + PageName.events,
        title: 'Events',
        icon: <Event />,
      },
    },
  },
});

export const getStudentsPageDetails = () => ({
  [PageName.students]: {
    path: '/' + PageName.students,
    title: 'Students',
    icon: <People />,
    subPages: {
      [PageName.studentList]: {
        path: '/' + PageName.studentList,
        title: 'All Students',
        icon: <PictureInPicture />,
      },
      [PageName.attendance]: {
        path: '/' + PageName.attendance,
        title: 'Attendance Records',
        icon: <ListAlt />,
      },
      [PageName.fees]: {
        path: '/' + PageName.fees,
        title: 'Fee Management',
        icon: <ReceiptLong />,
      },
      [PageName.events]: {
        path: '/' + PageName.events,
        title: 'Events',
        icon: <Event />,
      },
      [PageName.scores]: {
        path: '/' + PageName.scores,
        title: 'Scores',
        icon: <Score />,
      },
      [PageName.document]: {
        path: '/' + PageName.document,
        title: 'Document',
        icon: <DocumentScannerTwoTone />,
      },
    },
  },
});

export const getStaffPageDetails = () => ({
  [PageName.staff]: {
    path: '/' + PageName.staff,
    title: 'Staff',
    icon: <PeopleAltTwoTone />,
    subPages: {
      [PageName.staffList]: {
        path: '/' + PageName.staffList,
        title: 'All Staff',
        icon: <PictureInPicture />,
      },
      [PageName.attendance]: {
        path: '/' + PageName.attendance,
        title: 'Attendance',
        icon: <ListAlt />,
      },
      [PageName.payouts]: {
        path: '/' + PageName.payouts,
        title: 'Payouts',
        icon: <MonetizationOn />,
      },
      [PageName.events]: {
        path: '/' + PageName.events,
        title: 'Events',
        icon: <CalendarMonthSharp />,
      },
      [PageName.documents]: {
        path: '/' + PageName.documents,
        title: 'Documents',
        icon: <DocumentScannerTwoTone />,
      },
    },
  },
});

export const getAcademicsPageDetails = () => ({
  [PageName.academics]: {
    path: '/' + PageName.academics,
    title: 'Academics',
    icon: <LegendToggleRounded />,
    subPages: {
      [PageName.curriculum]: {
        path: '/' + PageName.curriculum,
        title: 'Classes and Courses',
        icon: <Book />,
      },
      [PageName.schedules]: {
        path: '/' + PageName.schedules,
        title: 'Schedules',
        icon: <CalendarMonthTwoTone />,
      },
      [PageName.lessonPlans]: {
        path: '/' + PageName.lessonPlans,
        title: 'Lesson Plans',
        icon: <Book />,
      },
      [PageName.materials]: {
        path: '/' + PageName.materials,
        title: 'Materials and Syllabus',
        icon: <CalendarMonthTwoTone />,
      },
      [PageName.assessments]: {
        path: '/' + PageName.assessments,
        title: 'Assessments',
        icon: <Score />,
      },
      [PageName.library]: {
        path: '/' + PageName.library,
        title: 'Library',
        icon: <BookOnlineTwoTone />,
      },
    },
  },
});

export const getCommunicationPageDetails = () => ({
  [PageName.communications]: {
    path: '/' + PageName.communications,
    title: 'Communications',
    icon: <ChatBubbleOutline />,
    subPages: {
      [PageName.messaging]: {
        path: '/' + PageName.messaging,
        title: 'Messaging',
        icon: <Chat />,
      },
      [PageName.announcements]: {
        path: '/' + PageName.announcements,
        title: 'Announcements',
        icon: <Speaker />,
      },
      [PageName.events]: {
        path: '/' + PageName.events,
        title: 'Events',
        icon: <CalendarMonthSharp />,
      },
      [PageName.meetings]: {
        path: '/' + PageName.meetings,
        title: 'Meetings',
        icon: <CalendarToday />,
      },
    },
  },
});

export const getFinancePageDetails = () => ({
  [PageName.finance]: {
    path: '/' + PageName.finance,
    title: 'Finance',
    icon: <Money />,
    subPages: {
      [PageName.financeList]: {
        path: '/' + PageName.financeList,
        title: 'Finance List',
        icon: <ListAltOutlined />,
      },
      [PageName.scholarships]: {
        path: '/' + PageName.scholarships,
        title: 'Scholarships and Grants',
        icon: <SchoolSharp />,
      },
      [PageName.financialReports]: {
        path: '/' + PageName.financialReports,
        title: 'Financial Reports',
        icon: <SchoolSharp />,
      },
    },
  },
});

export const getAdministrationPageDetails = () => ({
  [PageName.administration]: {
    path: '/' + PageName.administration,
    title: 'Administration',
    icon: <AdminPanelSettings />,
    subPages: {
      [PageName.transport]: {
        path: '/' + PageName.transport,
        title: 'Routes And Schedule',
        icon: <RouteOutlined />,
      },
      [PageName.inventory]: {
        path: '/' + PageName.inventory,
        title: 'Inventory',
        icon: <Inventory />,
      },
      [PageName.healthAndSafety]: {
        path: '/' + PageName.healthAndSafety,
        title: 'Health And Safety',
        icon: <SafetyCheck />,
      },
    },
  },
});

export const getAnalyticsPageDetails = () => ({
  [PageName.analytics]: {
    path: '/' + PageName.analytics,
    title: 'Analytics',
    icon: <Analytics />,
    subPages: {
      [PageName.performance]: {
        path: '/' + PageName.performance,
        title: 'Performance',
        icon: <SafetyCheck />,
      },
      [PageName.customDashboards]: {
        path: '/' + PageName.customDashboards,
        title: 'Custom Dashboards',
        icon: <DashboardCustomize />,
      },
    },
  },
});

export const getSettingsPageDetails = () => ({
  [PageName.settings]: {
    path: '/' + PageName.settings,
    title: 'Settings',
    icon: <Settings />,
    subPages: {
      [PageName.userManagement]: {
        path: '/' + PageName.userManagement,
        title: 'User Management',
        icon: <VerifiedUserTwoTone />,
      },
      [PageName.complianceManagement]: {
        path: '/' + PageName.complianceManagement,
        title: 'Compliance Management',
        icon: <DashboardCustomizeTwoTone />,
      },
    },
  },
});

export const getUsersPageDetails = () => ({
  [PageName.users]: {
    path: '/' + PageName.users,
    title: 'Users',
    icon: <SupervisorAccount />,
    subPages: {
      [PageName.parents]: {
        path: '/' + PageName.parents,
        title: 'Parents & Guardians',
        icon: <PeopleAlt />,
      },
      [PageName.visitors]: {
        path: '/' + PageName.visitors,
        title: 'Visitors',
        icon: <PersonAdd />,
      },
      [PageName.guests]: {
        path: '/' + PageName.guests,
        title: 'Guests & External',
        icon: <Group />,
      },
    },
  },
});

export const getPrivacyPageDetails = () => ({
  [PageName.privacy]: {
    path: '/' + PageName.privacy,
    title: 'Privacy',
    icon: <Dashboard />,
  },
});

export const getProfilePageDetails = () => ({
  [PageName.profile]: {
    path: '/' + PageName.profile,
    title: 'Profile',
    icon: <Dashboard />,
    subPages: {
      [PageName.personalDetails]: {
        path: '/' + PageName.personalDetails,
        title: 'Personal Details',
        icon: <Person />,
      },
      [PageName.documents]: {
        path: '/' + PageName.documents,
        title: 'Documents',
        icon: <DocumentScannerTwoTone />,
      },
      [PageName.calendar]: {
        path: '/' + PageName.calendar,
        title: 'Calendar',
        icon: <CalendarMonth />,
      },
      [PageName.leaves]: {
        path: '/' + PageName.leaves,
        title: 'Leaves',
        icon: <CalendarToday />,
      },
    },
  },
});

export const getLogoutPageDetails = () => ({
  [PageName.logout]: {
    path: '/' + PageName.logout,
    title: 'Logout',
    icon: <PowerOff />,
  },
});

export const getGuardianPageDetails = () => ({
  [PageName.guardian]: {
    path: '/' + PageName.guardian,
    title: 'Guardian',
    icon: <PeopleAlt />,
    subPages: {
      [PageName.dashboard]: {
        path: '/' + PageName.dashboard,
        title: 'Dashboard',
        icon: <Dashboard />,
      },
      [PageName.academics]: {
        path: '/' + PageName.academics,
        title: 'Academic',
        icon: <Book />,
        subPages: {
          [PageName.schedule]: {
            path: '/' + PageName.schedule,
            title: 'Timetable',
            icon: <CalendarMonthTwoTone />,
          },
          [PageName.curriculum]: {
            path: '/' + PageName.curriculum,
            title: 'Courses and Classes',
            icon: <SchoolSharp />,
          },
          [PageName.lessonPlans]: {
            path: '/' + PageName.lessonPlans,
            title: 'Lesson Plans',
            icon: <MenuBook />,
          },
          [PageName.materials]: {
            path: '/' + PageName.materials,
            title: 'Materials',
            icon: <MenuBook />,
          },
          [PageName.assessments]: {
            path: '/' + PageName.assessments,
            title: 'Assessments',
            icon: <Score />,
          },
          [PageName.homework]: {
            path: '/' + PageName.homework,
            title: 'Homework',
            icon: <BookOnlineTwoTone />,
          },
          [PageName.attendance]: {
            path: '/' + PageName.attendance,
            title: 'Attendance',
            icon: <ListAlt />,
          },
          [PageName.library]: {
            path: '/' + PageName.library,
            title: 'Library',
            icon: <BookOnlineTwoTone />,
          },
        },
      },
      [PageName.fees]: {
        path: '/' + PageName.fees,
        title: 'Fees',
        icon: <ReceiptLong />,
      },
      [PageName.transport]: {
        path: '/' + PageName.transport,
        title: 'Transport',
        icon: <RouteOutlined />,
      },
      [PageName.document]: {
        path: '/' + PageName.document,
        title: 'Documents',
        icon: <DocumentScannerTwoTone />,
      },
      [PageName.eventsAndCommunication]: {
        path: '/' + PageName.eventsAndCommunication,
        title: 'Events & Communication',
        icon: <ChatBubbleOutline />,
        subPages: {
          [PageName.events]: {
            path: '/' + PageName.events,
            title: 'Events',
            icon: <Event />,
          },
          [PageName.communications]: {
            path: '/' + PageName.communications,
            title: 'Communication',
            icon: <Chat />,
          },
        },
      },
      [PageName.concerns]: {
        path: '/' + PageName.concerns,
        title: 'My Concerns',
        icon: <Chat />,
      },
      [PageName.profile]: {
        path: '/' + PageName.profile,
        title: 'Profile',
        icon: <Person />,
      },
      [PageName.settings]: {
        path: '/' + PageName.settings,
        title: 'Settings',
        icon: <Settings />,
      },
      [PageName.logout]: {
        path: '/' + PageName.logout,
        title: 'Logout',
        icon: <PowerOff />,
      },
    },
  },
});
